import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Upload as KendoUpload } from "@progress/kendo-react-upload";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import Papa from "papaparse";

import ErrorBanner from "../../../components/ErrorBanner";
import { useQueryGetClientsMasterList } from "../../../data/services/clientAdminService";
import {
  useMutateAddOEWorkflowClient,
  useMutateAddOEWorkflowClientBulk,
} from "../../../data/services/workflowService";

const ACTIONS = {
  ADD: "Add",
  REMOVE: "Remove",
  UNCHANGED: "Unchanged",
  INVALID: "Invalid",
};

// Numeric-only IDs are zero-padded to 5 chars to match the workflow-wide convention (see SetRateGroupPanel).
const normalizeId = (raw) => {
  if (raw === undefined || raw === null) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  return /^\d+$/.test(trimmed) ? trimmed.padStart(5, "0") : trimmed;
};

const OEWorkflowImportClientsModel = ({
  oePeriodId,
  workflowSetupId,
  currentClients,
  onClose,
  onSubmit,
}) => {
  const [files, setFiles] = React.useState([]);
  const [parseError, setParseError] = React.useState(null);
  const [fileIds, setFileIds] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [applyError, setApplyError] = React.useState(null);

  const { data: availableClients } = useQueryGetClientsMasterList();
  const addBulkHandler = useMutateAddOEWorkflowClientBulk();
  const setClientHandler = useMutateAddOEWorkflowClient();

  const currentIncludedById = React.useMemo(() => {
    const map = new Map();
    (currentClients || []).forEach((c) => {
      if (c.included) map.set(normalizeId(c.clientId), c);
    });
    return map;
  }, [currentClients]);

  const validClientsById = React.useMemo(() => {
    const map = new Map();
    (availableClients?.data || []).forEach((c) => {
      map.set(normalizeId(c.clientId), c);
    });
    return map;
  }, [availableClients]);

  const buildPreview = React.useCallback(
    (fileIds) => {
      const seen = new Set();
      const fromFile = [];
      for (const id of fileIds) {
        if (!id || seen.has(id)) continue;
        seen.add(id);

        const inValid = validClientsById.get(id);
        const inCurrent = currentIncludedById.get(id);

        if (!inValid) {
          fromFile.push({
            clientId: id,
            clientName: inCurrent?.clientName || "(unknown)",
            action: ACTIONS.INVALID,
          });
        } else if (inCurrent) {
          fromFile.push({
            clientId: inValid.clientId,
            clientName: inValid.clientName,
            action: ACTIONS.UNCHANGED,
          });
        } else {
          fromFile.push({
            clientId: inValid.clientId,
            clientName: inValid.clientName,
            action: ACTIONS.ADD,
          });
        }
      }

      const removes = [];
      for (const [id, dataItem] of currentIncludedById.entries()) {
        if (!seen.has(id)) {
          removes.push({
            clientId: dataItem.clientId,
            clientName: dataItem.clientName,
            action: ACTIONS.REMOVE,
            existingRow: dataItem,
          });
        }
      }

      return [...fromFile, ...removes];
    },
    [currentIncludedById, validClientsById]
  );

  const previewRows = React.useMemo(
    () => (fileIds.length > 0 ? buildPreview(fileIds) : []),
    [fileIds, buildPreview]
  );

  const summary = React.useMemo(() => {
    const add = previewRows.filter((r) => r.action === ACTIONS.ADD);
    const remove = previewRows.filter((r) => r.action === ACTIONS.REMOVE);
    const unchanged = previewRows.filter(
      (r) => r.action === ACTIONS.UNCHANGED
    );
    const invalid = previewRows.filter((r) => r.action === ACTIONS.INVALID);
    return { add, remove, unchanged, invalid };
  }, [previewRows]);

  const canApply =
    !submitting &&
    !parseError &&
    (summary.add.length > 0 || summary.remove.length > 0);

  const onAddFileHandler = (event) => {
    setFiles(event.newState);
    setParseError(null);
    setFileIds([]);
    setApplyError(null);

    const rawFile = event.newState[0]?.getRawFile?.();
    if (!rawFile) return;

    Papa.parse(rawFile, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => (h || "").trim(),
      complete: (results) => {
        const headers = results.meta?.fields || [];
        const clientIdHeader = headers.find(
          (h) => h.toLowerCase() === "clientid"
        );

        if (!clientIdHeader) {
          setParseError(
            "The file is missing a required 'ClientId' column header."
          );
          setFileIds([]);
          return;
        }

        const ids = results.data
          .map((row) => normalizeId(row[clientIdHeader]))
          .filter((id) => id.length > 0);

        if (ids.length === 0) {
          setParseError("The file does not contain any Client Ids.");
          setFileIds([]);
          return;
        }

        setFileIds(ids);
      },
      error: () => {
        setParseError("Failed to parse the file. Please check the format.");
        setFileIds([]);
      },
    });
  };

  const onRemoveFileHandler = (event) => {
    setFiles(event.newState);
    setFileIds([]);
    setParseError(null);
    setApplyError(null);
  };

  const onDownloadTemplate = () => {
    const csvData = Papa.unparse([{ ClientId: "" }]);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Workflow Clients Import Template.csv";
    link.click();
  };

  const chunkedRemove = async (rowsToRemove) => {
    const failures = [];
    const CHUNK = 5;
    for (let i = 0; i < rowsToRemove.length; i += CHUNK) {
      const chunk = rowsToRemove.slice(i, i + CHUNK);
      const results = await Promise.allSettled(
        chunk.map((row) =>
          setClientHandler.mutateAsync({
            ...row.existingRow,
            oePeriodId,
            workflowSetupId,
            included: false,
          })
        )
      );
      results.forEach((r, idx) => {
        if (r.status === "rejected") failures.push(chunk[idx].clientId);
      });
    }
    return failures;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setApplyError(null);

    try {
      if (summary.add.length > 0) {
        await addBulkHandler.mutateAsync({
          oePeriodId,
          workflowSetupId,
          clients: summary.add.map((r) => r.clientId),
        });
      }

      let failedRemoves = [];
      if (summary.remove.length > 0) {
        failedRemoves = await chunkedRemove(summary.remove);
      }

      if (failedRemoves.length > 0) {
        setApplyError(
          `Failed to remove ${failedRemoves.length} client(s): ${failedRemoves.join(
            ", "
          )}. Please retry.`
        );
        setSubmitting(false);
        return;
      }

      setSubmitting(false);
      onSubmit();
    } catch {
      setSubmitting(false);
    }
  };

  const actionCell = (props) => {
    const action = props.dataItem.action;
    const color =
      action === ACTIONS.ADD
        ? "green"
        : action === ACTIONS.REMOVE
        ? "red"
        : action === ACTIONS.INVALID
        ? "#a94442"
        : "";
    return (
      <td
        style={{
          color,
          fontWeight: action === ACTIONS.INVALID ? "bold" : "",
        }}
      >
        {action}
      </td>
    );
  };

  return (
    <Dialog
      title={"Import clients to Workflow"}
      onClose={onClose}
      width={700}
    >
      <ErrorBanner handler={addBulkHandler} />
      <ErrorBanner handler={setClientHandler} />
      <p>
        Upload a CSV with a single <b>ClientId</b> column. Clients on the file
        that are not in the workflow will be added; clients currently in the
        workflow that are not on the file will be removed.
      </p>
      <p>
        <Button
          themeColor={"info"}
          fillMode="flat"
          onClick={onDownloadTemplate}
        >
          Download Template
        </Button>
      </p>
      <KendoUpload
        autoUpload={false}
        showActionButtons={false}
        files={files}
        multiple={false}
        onAdd={onAddFileHandler}
        onRemove={onRemoveFileHandler}
        restrictions={{ allowedExtensions: [".csv"] }}
      />
      {parseError && (
        <div
          style={{
            backgroundColor: "#f8d7da",
            padding: 10,
            marginTop: 10,
            borderRadius: ".25rem",
            color: "#721c24",
          }}
        >
          {parseError}
        </div>
      )}
      {previewRows.length > 0 && (
        <>
          <div style={{ marginTop: 20 }}>
            <b>{summary.add.length}</b> to add,{" "}
            <b>{summary.remove.length}</b> to remove,{" "}
            <b>{summary.unchanged.length}</b> unchanged,{" "}
            <b>{summary.invalid.length}</b> invalid.
          </div>
          {summary.invalid.length > 0 && (
            <div
              style={{
                backgroundColor: "#fff3cd",
                padding: 10,
                marginTop: 10,
                borderRadius: ".25rem",
                color: "#856404",
              }}
            >
              {summary.invalid.length} Client Id(s) in the file were not found
              in the master list and will be skipped.
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <Grid
              data={previewRows}
              style={{ maxHeight: "300px" }}
            >
              <Column field="clientId" title="Client Id" />
              <Column field="clientName" title="Client Name" />
              <Column
                field="action"
                title="Action"
                width={140}
                cell={actionCell}
              />
            </Grid>
          </div>
        </>
      )}
      {applyError && (
        <div
          style={{
            backgroundColor: "#f8d7da",
            padding: 10,
            marginTop: 10,
            borderRadius: ".25rem",
            color: "#721c24",
          }}
        >
          {applyError}
        </div>
      )}
      <div style={{ paddingTop: 20 }}></div>
      <DialogActionsBar layout={"end"}>
        <Button onClick={onClose}>{"Cancel"}</Button>
        <Button
          type={"submit"}
          themeColor={"success"}
          disabled={!canApply}
          onClick={handleSubmit}
        >
          {"Apply"}
          {submitting && <span className="k-icon k-i-loading"></span>}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default OEWorkflowImportClientsModel;
